import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  Nullable,
  Ruling,
  UserWithToken,
  WithData,
  WithMeta,
} from './api.types';
import { CreateRulingInput } from './rulings';

export type ArchetypeType = 'ARCHETYPE' | 'ENGINE';

export type Archetype = {
  id: number;
  name: string;
  type: ArchetypeType;
  description: string | null;
  /**
   * Cuántas cartas del catálogo son del arquetipo. Un arquetipo real con 2
   * cartas es dato viejo, no un arquetipo chico: lo mantiene al día la task
   * `SyncArchetypeCards`.
   */
  cardCount: number;
  /**
   * Carta sugerida para ilustrar al arquetipo: la suya más repetida en sus
   * decklists de ejemplo. `null` si no tiene decklists cargadas.
   */
  suggestedRepresentativeKonamiId: number | null;
  createdAt: string;
  updatedAt: string;
};

export type ArchetypeDeckCard = {
  code: number;
  name: string | null;
  quantity: number;
};

export type ArchetypeDecklist = {
  id: number;
  archetypeId: number;
  name: string;
  description: string | null;
  formatId: number | null;
  format: { id: number; name: string } | null;
  decks: {
    main: ArchetypeDeckCard[];
    side: ArchetypeDeckCard[];
    extra: ArchetypeDeckCard[];
  } | null;
  createdAt: string;
  updatedAt: string;
};

/** Una carta del arquetipo, con lo justo para listarla. */
export type ArchetypeCard = {
  id: number;
  konamiId: number;
  name: string;
  image?: string | null;
  frameType?: string | null;
};

export type ArchetypeCardSource = 'SYNC' | 'MANUAL' | 'EXCLUDED';

/**
 * Una carta del arquetipo con su origen. El origen distingue lo cargado a mano
 * de lo que trajo YGOProDeck: son lo mismo para prohibir, y no son lo mismo
 * para decidir si volver a sincronizar.
 */
export type ArchetypeCardEntry = {
  card: ArchetypeCard;
  source: ArchetypeCardSource;
  createdAt: string;
};

export type ArchetypeCardList = {
  id: number;
  name: string;
  cardCount: number;
  cards: ArchetypeCard[];
};

export type ArchetypeDetail = Archetype & {
  rulings: Ruling[];
  decklists: ArchetypeDecklist[];
  cards: ArchetypeCard[];
  cardEntries: ArchetypeCardEntry[];
  /**
   * Nombres de arquetipos fusionados en este (`mergeArchetypes`). Sólo para
   * el detalle: el alias no se lista ni se busca.
   */
  aliases: string[];
};

/** Cuántas filas de cada tabla movió (o descartó por repetidas) la fusión. */
export type MergeArchetypesResult = {
  cardsMoved: number;
  cardsDropped: number;
  banlistsMoved: number;
  banlistsDropped: number;
  rulingsMoved: number;
  decklistsMoved: number;
  metaDecksAsMainMoved: number;
  metaDeckEnginesMoved: number;
  metaDeckEnginesDropped: number;
  aliasesMoved: number;
  cardDisplayRenamed: number;
};

export type ArchetypeDecklistCardInput = {
  konamiId: number;
  deckType: 'MAIN' | 'SIDE' | 'EXTRA';
  quantity: number;
};

export type CreateArchetypeDecklistInput = {
  name: string;
  description?: string;
  formatId?: number;
  file?: string;
  cards?: ArchetypeDecklistCardInput[];
};

export type UpdateArchetypeDecklistInput =
  Partial<CreateArchetypeDecklistInput>;

const ARCHETYPE_DECKLIST_FIELDS = gql`
  fragment ArchetypeDecklistFields on ArchetypeDecklist {
    id
    archetypeId
    name
    description
    formatId
    format {
      id
      name
    }
    decks {
      main {
        code
        name
        quantity
      }
      side {
        code
        name
        quantity
      }
      extra {
        code
        name
        quantity
      }
    }
    createdAt
    updatedAt
  }
`;

export const getArchetypes = async (
  user: Nullable<UserWithToken>,
  filters: {
    type?: ArchetypeType;
    search?: string;
    page?: number;
    limit?: number;
  } = {}
) =>
  getGraphQLQueryClient(user).request<WithMeta<Archetype>>(
    gql`
      query archetypes($filters: ArchetypeFilters) {
        data: archetypes(filters: $filters) {
          data {
            id
            name
            type
            description
            cardCount
            suggestedRepresentativeKonamiId
            createdAt
            updatedAt
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `,
    { filters }
  );

/**
 * Sólo las cartas del arquetipo.
 *
 * Existe aparte de `getArchetype` porque quien arma una banlist quiere ver con
 * qué está trabajando antes de prohibir el grupo entero, y para eso no hacen
 * falta ni los rulings ni las decklists de ejemplo.
 */
export const getArchetypeCards = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<ArchetypeCardList>>(
    gql`
      query archetypeCards($id: Int!) {
        data: archetype(id: $id) {
          id
          name
          cardCount
          cards {
            id
            konamiId
            name
            image
            frameType
          }
        }
      }
    `,
    { id }
  );

/**
 * Las cartas de un arquetipo, sin sesión.
 *
 * Gemela de `getArchetypeCards`, que sí pide cuenta: la banlist pública la
 * mira un jugador sin login y `archetype` responde `not_authorized`.
 */
export const getPublicArchetypeCards = async (archetypeId: number) =>
  getGraphQLQueryClient(null).request<WithData<ArchetypeCardList>>(
    gql`
      query publicArchetypeCards($archetypeId: Int!) {
        data: publicArchetypeCards(archetypeId: $archetypeId) {
          id
          name
          cardCount
          cards {
            id
            konamiId
            name
            image
            frameType
          }
        }
      }
    `,
    { archetypeId }
  );

export const getArchetype = async (user: Nullable<UserWithToken>, id: number) =>
  getGraphQLQueryClient(user).request<WithData<ArchetypeDetail>>(
    gql`
      ${ARCHETYPE_DECKLIST_FIELDS}
      query archetype($id: Int!) {
        data: archetype(id: $id) {
          id
          name
          type
          description
          cardCount
          aliases
          createdAt
          updatedAt
          cards {
            id
            konamiId
            name
            image
            frameType
          }
          cardEntries {
            source
            createdAt
            card {
              id
              konamiId
              name
              image
              frameType
            }
          }
          rulings {
            id
            title
            description
            type
            isActive
            cards { id konamiId name image }
            sources { id url label }
          }
          decklists {
            ...ArchetypeDecklistFields
          }
        }
      }
    `,
    { id }
  );

export const createArchetype = async (
  user: Nullable<UserWithToken>,
  input: {
    name: string;
    type: ArchetypeType;
    description?: string;
  }
) =>
  getGraphQLQueryClient(user).request<WithData<Archetype>>(
    gql`
      mutation createArchetype($input: CreateArchetypeInput!) {
        data: createArchetype(input: $input) {
          id
          name
          type
          description
        }
      }
    `,
    { input }
  );

export const updateArchetype = async (
  user: Nullable<UserWithToken>,
  id: number,
  input: {
    name?: string;
    type?: ArchetypeType;
    description?: string;
  }
) =>
  getGraphQLQueryClient(user).request<WithData<Archetype>>(
    gql`
      mutation updateArchetype($id: Int!, $input: UpdateArchetypeInput!) {
        data: updateArchetype(id: $id, input: $input) {
          id
          name
          type
          description
        }
      }
    `,
    { id, input }
  );

export const deleteArchetype = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation deleteArchetype($id: Int!) {
        data: deleteArchetype(id: $id)
      }
    `,
    { id }
  );

/**
 * Fusiona `sourceId` en `targetId`: todo pasa al destino y el origen queda
 * como alias suyo, invisible en la UI. Sólo admin.
 */
export const mergeArchetypes = async (
  user: Nullable<UserWithToken>,
  sourceId: number,
  targetId: number
) =>
  getGraphQLQueryClient(user).request<WithData<MergeArchetypesResult>>(
    gql`
      mutation mergeArchetypes($sourceId: Int!, $targetId: Int!) {
        data: mergeArchetypes(sourceId: $sourceId, targetId: $targetId) {
          cardsMoved
          cardsDropped
          banlistsMoved
          banlistsDropped
          rulingsMoved
          decklistsMoved
          metaDecksAsMainMoved
          metaDeckEnginesMoved
          metaDeckEnginesDropped
          aliasesMoved
          cardDisplayRenamed
        }
      }
    `,
    { sourceId, targetId }
  );

export const linkArchetypeRuling = async (
  user: Nullable<UserWithToken>,
  archetypeId: number,
  rulingId: number
) =>
  getGraphQLQueryClient(user).request<WithData<{ id: number }>>(
    gql`
      mutation linkArchetypeRuling($archetypeId: Int!, $rulingId: Int!) {
        data: linkArchetypeRuling(
          archetypeId: $archetypeId
          rulingId: $rulingId
        ) {
          id
        }
      }
    `,
    { archetypeId, rulingId }
  );

export const unlinkArchetypeRuling = async (
  user: Nullable<UserWithToken>,
  archetypeId: number,
  rulingId: number
) =>
  getGraphQLQueryClient(user).request<WithData<{ id: number }>>(
    gql`
      mutation unlinkArchetypeRuling($archetypeId: Int!, $rulingId: Int!) {
        data: unlinkArchetypeRuling(
          archetypeId: $archetypeId
          rulingId: $rulingId
        ) {
          id
        }
      }
    `,
    { archetypeId, rulingId }
  );

export const addCardsToArchetype = async (
  user: Nullable<UserWithToken>,
  archetypeId: number,
  konamiIds: number[]
) =>
  getGraphQLQueryClient(user).request<WithData<{ id: number }>>(
    gql`
      mutation addCardsToArchetype($archetypeId: Int!, $konamiIds: [Int!]!) {
        data: addCardsToArchetype(
          archetypeId: $archetypeId
          konamiIds: $konamiIds
        ) {
          id
        }
      }
    `,
    { archetypeId, konamiIds }
  );

/**
 * `exclude` deja lápida para que `SyncArchetypeCards` no reponga la carta. Sin
 * ella, quitar una carta que YGOProDeck asigna dura hasta la próxima corrida.
 */
export const removeCardsFromArchetype = async (
  user: Nullable<UserWithToken>,
  archetypeId: number,
  konamiIds: number[],
  exclude = false
) =>
  getGraphQLQueryClient(user).request<WithData<{ id: number }>>(
    gql`
      mutation removeCardsFromArchetype(
        $archetypeId: Int!
        $konamiIds: [Int!]!
        $exclude: Boolean
      ) {
        data: removeCardsFromArchetype(
          archetypeId: $archetypeId
          konamiIds: $konamiIds
          exclude: $exclude
        ) {
          id
        }
      }
    `,
    { archetypeId, konamiIds, exclude }
  );

export const setCardArchetypes = async (
  user: Nullable<UserWithToken>,
  konamiId: number,
  archetypeIds: number[]
) =>
  getGraphQLQueryClient(user).request<WithData<{ konamiId: number }>>(
    gql`
      mutation setCardArchetypes($konamiId: Int!, $archetypeIds: [Int!]!) {
        data: setCardArchetypes(
          konamiId: $konamiId
          archetypeIds: $archetypeIds
        ) {
          konamiId
        }
      }
    `,
    { konamiId, archetypeIds }
  );

export const createArchetypeRuling = async (
  user: Nullable<UserWithToken>,
  archetypeId: number,
  input: CreateRulingInput
) =>
  getGraphQLQueryClient(user).request<WithData<Ruling>>(
    gql`
      mutation createArchetypeRuling(
        $archetypeId: Int!
        $input: CreateRulingInput!
      ) {
        data: createArchetypeRuling(archetypeId: $archetypeId, input: $input) {
          id
          title
          description
          type
          isActive
          cards { id konamiId name image }
        }
      }
    `,
    { archetypeId, input }
  );

export const createArchetypeDecklist = async (
  user: Nullable<UserWithToken>,
  archetypeId: number,
  input: CreateArchetypeDecklistInput
) =>
  getGraphQLQueryClient(user).request<WithData<ArchetypeDecklist>>(
    gql`
      ${ARCHETYPE_DECKLIST_FIELDS}
      mutation createArchetypeDecklist(
        $archetypeId: Int!
        $input: CreateArchetypeDecklistInput!
      ) {
        data: createArchetypeDecklist(archetypeId: $archetypeId, input: $input) {
          ...ArchetypeDecklistFields
        }
      }
    `,
    { archetypeId, input }
  );

export const updateArchetypeDecklist = async (
  user: Nullable<UserWithToken>,
  id: number,
  input: UpdateArchetypeDecklistInput
) =>
  getGraphQLQueryClient(user).request<WithData<ArchetypeDecklist>>(
    gql`
      ${ARCHETYPE_DECKLIST_FIELDS}
      mutation updateArchetypeDecklist(
        $id: Int!
        $input: UpdateArchetypeDecklistInput!
      ) {
        data: updateArchetypeDecklist(id: $id, input: $input) {
          ...ArchetypeDecklistFields
        }
      }
    `,
    { id, input }
  );

export const deleteArchetypeDecklist = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation deleteArchetypeDecklist($id: Int!) {
        data: deleteArchetypeDecklist(id: $id)
      }
    `,
    { id }
  );
